import React from "react";
import { useQuery } from "@apollo/client";
import ContentLoader from "react-content-loader";
import { ErrorView } from "../../Components/RecurringSmallViews/RecurringSmallViews";

import {
  Get_Homepage_PostsQuery,
  Get_Homepage_PostsQueryVariables,
} from "../../API";

import { GetHomepagePosts } from "../../graphql/customWebappQueries/homepageQueries";

import { makePostNavigatingTawkPillConfig } from "../../Infrastucture/ComponentConfigBackendBridge";

import "../../Infrastucture/MissingStd";
import "./HorizontalScroller_All.css";

import {
  HomePageHorizontalScroller,
  HomePageHorizontalScrollerConfig,
} from "./HorizontalScroller_Individual";

import { PostNavigatingTawkPillConfig } from "../../Components/PostNavigatingTawkPill/PostNavigatingTawkPill";

export interface HomepageScrollersConfig {
  scroller1Config: HomePageHorizontalScrollerConfig;
  scroller2Config: HomePageHorizontalScrollerConfig;
  scroller3Config: HomePageHorizontalScrollerConfig;
}

function Scrollers(props: HomepageScrollersConfig): JSX.Element {
  return (
    <div className="scrollers-container">
      <HomePageHorizontalScroller {...props.scroller1Config} />
      <HomePageHorizontalScroller {...props.scroller2Config} />
      <HomePageHorizontalScroller {...props.scroller3Config} />
    </div>
  );
}

function ScrollersGivenPostConfigs(
  postConfigs: PostNavigatingTawkPillConfig[]
): JSX.Element {
  const whatIsTawkit = postConfigs[0];
  const scrollersConfig: HomepageScrollersConfig = {
    scroller1Config: {
      preferredPillConfig: whatIsTawkit,
      tawkPillConfigs: postConfigs.slice(1, 7),
      offsetLevel: 0,
    },
    scroller2Config: {
      preferredPillConfig: null,
      tawkPillConfigs: postConfigs.slice(7, 14),
      offsetLevel: 1,
    },
    scroller3Config: {
      preferredPillConfig: null,
      tawkPillConfigs: postConfigs.slice(14, undefined),
      offsetLevel: 2,
    },
  };

  return Scrollers(scrollersConfig);
}

export function LoadingScrollers(): JSX.Element {
  const { loading, error, data } = useQuery<
    Get_Homepage_PostsQuery,
    Get_Homepage_PostsQueryVariables
  >(GetHomepagePosts, {
    errorPolicy: "ignore",
    variables: {
      post1: "8cf2e4d1-e052-45a2-b14d-69d99333f9b7", // what is tawkit
      post2: "5cfd7926-2887-490c-8476-a5bfa9e04047",
      post3: "6e813905-65c7-4fdf-82b4-3bda6e9aacf6",
      post4: "e7827442-156e-40fd-939f-b7521173544e",
      post5: "a1e94cdd-2230-4f98-ab3d-3fff34474a23",
      post6: "0eb612d8-9aa7-4bed-838e-980dafe9490b",
      post7: "eb7c87ca-caff-4b1d-9a0b-1916e9650efa",
      post8: "3c2955a6-abba-4bb8-9301-2ae65dba76d4",
      post9: "a683421b-0038-4983-9c91-743bcdfd4003",
      post11: "1d0c4e2e-ee64-4593-b022-d7d8fe84e816",
      post10: "357689ec-5afb-45b0-a489-88f7f9dab761",
      post12: "15719957-a69b-4681-82db-1a1505c99343",
      post13: "54ad2f7e-e80b-43c7-9263-903cf3e6904a",
      post14: "c385e1fb-223f-4d2e-ba32-b91e0122e708",
      post15: "42cda60c-704e-4124-84fa-1eedac885169",
      post16: "d1906a82-8e4e-4ee3-b090-8e2cc43fb13f",
      post17: "9e9f966c-427a-4a65-8e3a-0b1fca14f025",
      post18: "847b3ca4-6ae4-4735-a63c-d6b6719e5112",
      post19: "9de91027-13cb-435f-85e1-837dc930a0d7",
      post20: "b372f172-9c0a-4e78-9ed1-b9af2d424cca",
      post21: "1be8fb1c-1931-4c58-9270-889bf7d9396b",
      post22: "975900aa-e434-47a9-864b-7092b6a124f6",
      post23: "12c6d16c-ed2d-4e2e-bcdc-adc5a3dd9cf4",
    },
  });

  if (loading) {
    return <ContentLoader height={190} />; // 160 is the height of the scroller element; top scroller is 60px tall, next 2 are 45px tall each, with 20px top padding; 60 + 2(45 + 20) = 190.
  }
  if (error) {
    return ErrorView();
  }

  const postConfigs = [
    data?.post1,
    data?.post2,
    data?.post3,
    data?.post4,
    data?.post5,
    data?.post6,
    data?.post7,
    data?.post8,
    data?.post9,
    data?.post10,
    data?.post11,
    data?.post12,
    data?.post13,
    data?.post14,
    data?.post15,
    data?.post16,
    data?.post17,
    data?.post18,
    data?.post19,
    data?.post20,
    data?.post21,
    data?.post22,
    data?.post23,
  ]
    .tawkitExtensions()
    .compactMap((item) => {
      if (item) {
        const config = makePostNavigatingTawkPillConfig(item);
        if (config) {
          config.tawkPillConfig.size = "normal";
        }

        return config;
      }
      return null;
    });

  const whatIsTawkit = postConfigs[0];
  whatIsTawkit.tawkPillConfig.size = "big";
  whatIsTawkit.tawkPillConfig.colorsProvider.color2StandardPill = "#6125DC";
  whatIsTawkit.tawkPillConfig.colorsProvider.color6TextItself = "white";

  // Uncomment tog et
  // console.log(posts);

  return ScrollersGivenPostConfigs(postConfigs);
}

export function PreLoadedScrollers(): JSX.Element {
  // NOTE: the configs below were created by running LoadingScroller component above,
  // and copy-pasting the printed postConfigs array into the below.
  const preLoadedPostConfigs: PostNavigatingTawkPillConfig[] = [
    {
      tawkPillConfig: {
        tawkTitle: "What is tawkit? v1.0 release edition",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/jj1llivb0ff1axzkd46o",
        },
        authorUserHandle: "atai",
        colorsProvider: {
          color1HorizontalSlider: "rgb(249, 255, 245)",
          color2StandardPill: "#6125DC",
          color3TawkPillPortionAlreadyPlayed: "rgb(225, 252, 202)",
          color4PlaybackScrubber: "rgb(210, 247, 178)",
          color5TextBubble: "rgb(249, 255, 245)",
          color6TextItself: "white",
        },
        durationDescription: "20:22",
        isClip: false,
        size: "big",
      },
      postId: "8cf2e4d1-e052-45a2-b14d-69d99333f9b7",
    },
    {
      tawkPillConfig: {
        tawkTitle:
          "We will soon enter a period in which the tech Billionaires step-up",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/ttcswtbfzss48ta6a9ri",
        },
        authorUserHandle: "ulidabess",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 252, 245)",
          color2StandardPill: "rgb(252, 244, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 240, 202)",
          color4PlaybackScrubber: "rgb(247, 230, 178)",
          color5TextBubble: "rgb(255, 252, 245)",
          color6TextItself: "black",
        },
        durationDescription: "5:41",
        isClip: false,
        size: "normal",
      },
      postId: "5cfd7926-2887-490c-8476-a5bfa9e04047",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Nash Equilibrium",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/icccuh2rfxkjegp2b6lo",
        },
        authorUserHandle: "tawkit",
        colorsProvider: {
          color1HorizontalSlider: "rgb(251, 255, 245)",
          color2StandardPill: "rgb(237, 252, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(231, 252, 202)",
          color4PlaybackScrubber: "rgb(218, 247, 178)",
          color5TextBubble: "rgb(251, 255, 245)",
          color6TextItself: "black",
        },
        durationDescription: "7:37",
        isClip: false,
        size: "normal",
      },
      postId: "6e813905-65c7-4fdf-82b4-3bda6e9aacf6",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Vitalik Future is Multichain not Crosschiaj part 2",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/default",
        },
        authorUserHandle: "alextoberoff",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 249, 255)",
          color2StandardPill: "rgb(217, 232, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 223, 252)",
          color4PlaybackScrubber: "rgb(178, 207, 247)",
          color5TextBubble: "rgb(245, 249, 255)",
          color6TextItself: "black",
        },
        durationDescription: "2:47",
        isClip: false,
        size: "normal",
      },
      postId: "e7827442-156e-40fd-939f-b7521173544e",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Intro to NFTs",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/gmtiz7q4fxclgegahnqk",
        },
        authorUserHandle: "ryanshon",
        colorsProvider: {
          color1HorizontalSlider: "rgb(253, 245, 255)",
          color2StandardPill: "rgb(246, 217, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(243, 202, 252)",
          color4PlaybackScrubber: "rgb(234, 178, 247)",
          color5TextBubble: "rgb(253, 245, 255)",
          color6TextItself: "black",
        },
        durationDescription: "6:26",
        isClip: false,
        size: "normal",
      },
      postId: "a1e94cdd-2230-4f98-ab3d-3fff34474a23",
    },
    {
      tawkPillConfig: {
        tawkTitle:
          "Ep.20 understanding the new wave of conservatism with Gadi Taub",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/cukuhqgexue873dcgmaq",
        },
        authorUserHandle: "Carmel",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 248, 255)",
          color2StandardPill: "rgb(217, 227, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 216, 252)",
          color4PlaybackScrubber: "rgb(178, 197, 247)",
          color5TextBubble: "rgb(245, 248, 255)",
          color6TextItself: "black",
        },
        durationDescription: "5:34",
        isClip: false,
        size: "normal",
      },
      postId: "0eb612d8-9aa7-4bed-838e-980dafe9490b",
    },
    {
      tawkPillConfig: {
        tawkTitle: "3 observations about our current inflation",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/ttcswtbfzss48ta6a9ri",
        },
        authorUserHandle: "ulidabess",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 253, 245)",
          color2StandardPill: "rgb(252, 245, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 241, 202)",
          color4PlaybackScrubber: "rgb(247, 232, 178)",
          color5TextBubble: "rgb(255, 253, 245)",
          color6TextItself: "black",
        },
        durationDescription: "3:34",
        isClip: false,
        size: "normal",
      },
      postId: "3c2955a6-abba-4bb8-9301-2ae65dba76d4",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Can Futurism & Fascism be decoupled?",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/cukuhqgexue873dcgmaq",
        },
        authorUserHandle: "Carmel",
        colorsProvider: {
          color1HorizontalSlider: "rgb(251, 245, 255)",
          color2StandardPill: "rgb(238, 217, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(232, 202, 252)",
          color4PlaybackScrubber: "rgb(219, 178, 247)",
          color5TextBubble: "rgb(251, 245, 255)",
          color6TextItself: "black",
        },
        durationDescription: "4:20",
        isClip: false,
        size: "normal",
      },
      postId: "a683421b-0038-4983-9c91-743bcdfd4003",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Consciousness & Qualia: A Short Reflection",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/eew84uekexqprpqwhlup",
        },
        authorUserHandle: "sanjanasingh",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 248, 255)",
          color2StandardPill: "rgb(217, 229, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 219, 252)",
          color4PlaybackScrubber: "rgb(178, 202, 247)",
          color5TextBubble: "rgb(245, 248, 255)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "357689ec-5afb-45b0-a489-88f7f9dab761",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Chronicles of a Kiddie Doc",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/default",
        },
        authorUserHandle: "nuphaloof",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 251, 245)",
          color2StandardPill: "rgb(252, 239, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 234, 202)",
          color4PlaybackScrubber: "rgb(247, 222, 178)",
          color5TextBubble: "rgb(255, 251, 245)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "1d0c4e2e-ee64-4593-b022-d7d8fe84e816",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Being a Nudnik is Good for Success",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/dxegqp6solxnguyqt1cx",
        },
        authorUserHandle: "JonnyTheAstrologer",
        colorsProvider: {
          color1HorizontalSlider: "rgb(253, 255, 245)",
          color2StandardPill: "rgb(246, 252, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(243, 252, 202)",
          color4PlaybackScrubber: "rgb(234, 247, 178)",
          color5TextBubble: "rgb(253, 255, 245)",
          color6TextItself: "black",
        },
        durationDescription: "4:37",
        isClip: false,
        size: "normal",
      },
      postId: "15719957-a69b-4681-82db-1a1505c99343",
    },
    {
      tawkPillConfig: {
        tawkTitle: "On Angels & Algorithms",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/cukuhqgexue873dcgmaq",
        },
        authorUserHandle: "Carmel",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 255, 247)",
          color2StandardPill: "rgb(217, 252, 226)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 252, 215)",
          color4PlaybackScrubber: "rgb(178, 247, 195)",
          color5TextBubble: "rgb(245, 255, 247)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "54ad2f7e-e80b-43c7-9263-903cf3e6904a",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Harry Potter books 1 flaw",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/w8mplyle380yeqkzzitr",
        },
        authorUserHandle: "Yodinak",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 245, 254)",
          color2StandardPill: "rgb(252, 217, 249)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 202, 248)",
          color4PlaybackScrubber: "rgb(247, 178, 241)",
          color5TextBubble: "rgb(255, 245, 254)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "c385e1fb-223f-4d2e-ba32-b91e0122e708",
    },
    {
      tawkPillConfig: {
        tawkTitle:
          "It will be illegal for a human to operate a motor vehicle on a US highway within our lifetimes",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/default",
        },
        authorUserHandle: "Me",
        colorsProvider: {
          color1HorizontalSlider: "rgb(250, 255, 245)",
          color2StandardPill: "rgb(236, 252, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(230, 252, 202)",
          color4PlaybackScrubber: "rgb(216, 247, 178)",
          color5TextBubble: "rgb(250, 255, 245)",
          color6TextItself: "black",
        },
        durationDescription: "2:09",
        isClip: false,
        size: "normal",
      },
      postId: "42cda60c-704e-4124-84fa-1eedac885169",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Propaganda? ⛑",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/eb6wb6hqifs8cz2b9b5j",
        },
        authorUserHandle: "Paula",
        colorsProvider: {
          color1HorizontalSlider: "rgb(252, 245, 255)",
          color2StandardPill: "rgb(241, 217, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(235, 202, 252)",
          color4PlaybackScrubber: "rgb(224, 178, 247)",
          color5TextBubble: "rgb(252, 245, 255)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "d1906a82-8e4e-4ee3-b090-8e2cc43fb13f",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Chopin etude in g flat major op. 10 no. 5",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/default",
        },
        authorUserHandle: "Urizaf",
        colorsProvider: {
          color1HorizontalSlider: "rgb(246, 255, 245)",
          color2StandardPill: "rgb(220, 252, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(206, 252, 202)",
          color4PlaybackScrubber: "rgb(184, 247, 178)",
          color5TextBubble: "rgb(246, 255, 245)",
          color6TextItself: "black",
        },
        durationDescription: "1:47",
        isClip: false,
        size: "normal",
      },
      postId: "9e9f966c-427a-4a65-8e3a-0b1fca14f025",
    },
    {
      tawkPillConfig: {
        tawkTitle: "3 layers of the medium is the message",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/ttcswtbfzss48ta6a9ri",
        },
        authorUserHandle: "ulidabess",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 250, 245)",
          color2StandardPill: "rgb(252, 235, 217)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 228, 202)",
          color4PlaybackScrubber: "rgb(247, 213, 178)",
          color5TextBubble: "rgb(255, 250, 245)",
          color6TextItself: "black",
        },
        durationDescription: "10:01",
        isClip: false,
        size: "normal",
      },
      postId: "847b3ca4-6ae4-4735-a63c-d6b6719e5112",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Excerpt 2 rchmaninoff concerto 3",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/default",
        },
        authorUserHandle: "Urizaf",
        colorsProvider: {
          color1HorizontalSlider: "rgb(255, 245, 247)",
          color2StandardPill: "rgb(252, 217, 225)",
          color3TawkPillPortionAlreadyPlayed: "rgb(252, 202, 213)",
          color4PlaybackScrubber: "rgb(247, 178, 193)",
          color5TextBubble: "rgb(255, 245, 247)",
          color6TextItself: "black",
        },
        durationDescription: "2:08",
        isClip: false,
        size: "normal",
      },
      postId: "9de91027-13cb-435f-85e1-837dc930a0d7",
    },
    {
      tawkPillConfig: {
        tawkTitle: "On the Multiverse",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/eew84uekexqprpqwhlup",
        },
        authorUserHandle: "sanjanasingh",
        colorsProvider: {
          color1HorizontalSlider: "rgb(254, 245, 255)",
          color2StandardPill: "rgb(251, 217, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(250, 202, 252)",
          color4PlaybackScrubber: "rgb(244, 178, 247)",
          color5TextBubble: "rgb(254, 245, 255)",
          color6TextItself: "black",
        },
        durationDescription: "3:25",
        isClip: false,
        size: "normal",
      },
      postId: "b372f172-9c0a-4e78-9ed1-b9af2d424cca",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Fast space travel",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/jj1llivb0ff1axzkd46o",
        },
        authorUserHandle: "atai",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 255, 245)",
          color2StandardPill: "rgb(217, 252, 218)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 252, 203)",
          color4PlaybackScrubber: "rgb(178, 247, 179)",
          color5TextBubble: "rgb(245, 255, 245)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "1be8fb1c-1931-4c58-9270-889bf7d9396b",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Johnny Depp and the world of nonsense",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/dxegqp6solxnguyqt1cx",
        },
        authorUserHandle: "JonnyTheAstrologer",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 255, 251)",
          color2StandardPill: "rgb(217, 252, 239)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 252, 234)",
          color4PlaybackScrubber: "rgb(178, 247, 222)",
          color5TextBubble: "rgb(245, 255, 251)",
          color6TextItself: "black",
        },
        durationDescription: "1:53",
        isClip: false,
        size: "normal",
      },
      postId: "975900aa-e434-47a9-864b-7092b6a124f6",
    },
    {
      tawkPillConfig: {
        tawkTitle: "Did science fiction loose its future?",
        authorProfilePicConfig: {
          kind: "CloudinaryImageConfig",
          publicId: "ProfilePictures/cukuhqgexue873dcgmaq",
        },
        authorUserHandle: "Carmel",
        colorsProvider: {
          color1HorizontalSlider: "rgb(245, 255, 255)",
          color2StandardPill: "rgb(217, 251, 252)",
          color3TawkPillPortionAlreadyPlayed: "rgb(202, 250, 252)",
          color4PlaybackScrubber: "rgb(178, 244, 247)",
          color5TextBubble: "rgb(245, 255, 255)",
          color6TextItself: "black",
        },
        durationDescription: "5:20",
        isClip: false,
        size: "normal",
      },
      postId: "12c6d16c-ed2d-4e2e-bcdc-adc5a3dd9cf4",
    },
  ];

  return ScrollersGivenPostConfigs(preLoadedPostConfigs);
}
